import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, LoaderCircle, ShieldCheck, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { apiRequest, errorMessage } from '../../lib/api';
import { accountSchema, okSchema, twoFactorSetupSchema } from '../../lib/schemas';
import { queryKeys } from '../../lib/queryClient';

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
}

type Feedback = { text: string; kind: 'ok' | 'error' } | null;

export function AccountDialog({ open, onClose }: AccountDialogProps) {
  const queryClient = useQueryClient();
  const account = useQuery({
    queryKey: queryKeys.account,
    queryFn: () => apiRequest('/api/account', accountSchema),
    enabled: open,
    retry: false,
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFeedback, setPasswordFeedback] = useState<Feedback>(null);
  const [enrollment, setEnrollment] = useState<{ secret: string; qr: string | null } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorFeedback, setTwoFactorFeedback] = useState<Feedback>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableFeedback, setDisableFeedback] = useState<Feedback>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, open]);

  const passwordMutation = useMutation({
    mutationFn: () => apiRequest('/api/account/password', okSchema, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
      handleUnauthorized: false,
    }),
  });
  const setupMutation = useMutation({
    mutationFn: () => apiRequest('/api/account/2fa/setup', twoFactorSetupSchema, { method: 'POST' }),
  });
  const enableMutation = useMutation({
    mutationFn: () => apiRequest('/api/account/2fa/enable', okSchema, {
      method: 'POST',
      body: JSON.stringify({ code: twoFactorCode.trim() }),
    }),
  });
  const disableMutation = useMutation({
    mutationFn: () => apiRequest('/api/account/2fa/disable', okSchema, {
      method: 'POST',
      body: JSON.stringify({ password: disablePassword }),
      handleUnauthorized: false,
    }),
  });

  if (!open) return null;

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 6) {
      setPasswordFeedback({ text: 'New password must be at least 6 characters.', kind: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordFeedback({ text: 'New passwords do not match.', kind: 'error' });
      return;
    }
    setPasswordFeedback(null);
    try {
      await passwordMutation.mutateAsync();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordFeedback({ text: 'Password updated', kind: 'ok' });
    } catch (error) {
      setPasswordFeedback({ text: errorMessage(error, 'Password update failed.'), kind: 'error' });
    }
  }

  async function startEnrollment() {
    setTwoFactorFeedback(null);
    try {
      const result = await setupMutation.mutateAsync();
      setEnrollment({ secret: result.secret, qr: result.qr });
    } catch (error) {
      setTwoFactorFeedback({ text: errorMessage(error, 'Could not start 2FA setup.'), kind: 'error' });
    }
  }

  async function enableTwoFactor() {
    try {
      await enableMutation.mutateAsync();
      setEnrollment(null);
      setTwoFactorCode('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.account });
    } catch (error) {
      setTwoFactorFeedback({ text: errorMessage(error, 'That code is not valid.'), kind: 'error' });
    }
  }

  async function disableTwoFactor() {
    try {
      await disableMutation.mutateAsync();
      setDisablePassword('');
      setDisableFeedback(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.account });
    } catch (error) {
      setDisableFeedback({ text: errorMessage(error, 'Could not turn off 2FA.'), kind: 'error' });
    }
  }

  const twoFactorEnabled = Boolean(account.data?.twoFactorEnabled);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="dialog-card account-dialog" role="dialog" aria-modal="true" aria-labelledby="account-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="dialog-header">
          <div><h2 id="account-title">Account</h2><p>{account.data?.username || 'Your account settings'}</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close account settings" title="Close"><X size={19} /></button>
        </header>

        {account.isError ? <div className="inline-error">{errorMessage(account.error, 'Could not load account settings.')}</div> : null}

        <form className="account-section" onSubmit={changePassword}>
          <h3>Change password</h3>
          <input aria-label="Current password" type="password" autoComplete="current-password" placeholder="Current password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
          <input aria-label="New password" type="password" autoComplete="new-password" placeholder="New password (min 6 characters)" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
          <input aria-label="Confirm new password" type="password" autoComplete="new-password" placeholder="Confirm new password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
          <button className="primary-button compact" disabled={passwordMutation.isPending} type="submit">{passwordMutation.isPending ? <LoaderCircle className="spin" size={16} /> : null}Update password</button>
          <FeedbackLine feedback={passwordFeedback} />
        </form>

        <section className="account-section">
          <div className="account-title-row">
            <h3>Two-factor authentication</h3>
            <span className={`status-pill ${twoFactorEnabled ? 'on' : ''}`}>{twoFactorEnabled ? 'On' : 'Off'}</span>
          </div>

          {account.isPending ? <p className="muted-row"><LoaderCircle className="spin" size={15} />Loading security status</p> : null}

          {!account.isPending && !twoFactorEnabled && !enrollment ? (
            <>
              <p>Add a second sign-in step using an authenticator app.</p>
              <button className="secondary-button" type="button" disabled={setupMutation.isPending} onClick={startEnrollment}><ShieldCheck size={16} />Enable 2FA</button>
            </>
          ) : null}

          {enrollment ? (
            <div className="enrollment">
              <p>Scan this with your authenticator app, then enter the 6-digit code.</p>
              {enrollment.qr ? <img src={enrollment.qr} alt="Two-factor authentication QR code" /> : null}
              <p>Cannot scan? Enter this key manually:</p>
              <code>{enrollment.secret}</code>
              <input aria-label="Authenticator code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code" value={twoFactorCode} onChange={(event) => setTwoFactorCode(event.target.value)} />
              <div className="button-row">
                <button className="secondary-button" type="button" onClick={() => { setEnrollment(null); setTwoFactorFeedback(null); }}>Cancel</button>
                <button className="primary-button compact" type="button" disabled={enableMutation.isPending} onClick={enableTwoFactor}>{enableMutation.isPending ? <LoaderCircle className="spin" size={16} /> : null}Confirm & turn on</button>
              </div>
              <FeedbackLine feedback={twoFactorFeedback} />
            </div>
          ) : null}

          {twoFactorEnabled ? (
            <div className="disable-two-factor">
              <p className="success-row"><Check size={15} />Two-factor is on. You will enter a code each time you sign in.</p>
              <input aria-label="Password to disable two-factor authentication" type="password" autoComplete="current-password" placeholder="Current password to turn off" value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} />
              <button className="danger-button" type="button" disabled={disableMutation.isPending} onClick={disableTwoFactor}>Turn off 2FA</button>
              <FeedbackLine feedback={disableFeedback} />
            </div>
          ) : null}
        </section>
      </section>
    </div>
  );
}

function FeedbackLine({ feedback }: { feedback: Feedback }) {
  return <div className={`feedback-line ${feedback?.kind || ''}`} role={feedback?.kind === 'error' ? 'alert' : 'status'}>{feedback?.text || ''}</div>;
}
